// Framework-level routing errors (e.g. hitting an endpoint the backend hasn't deployed
// yet) come back from Nest's default handler as { message: "Cannot GET /some/path" } —
// technically a string in `.message`, but not something an admin user should ever see
// verbatim. Only surface the backend message when it looks like an actual app error.
export function getErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (typeof message !== 'string' || /^Cannot (GET|POST|PATCH|PUT|DELETE) /.test(message)) {
    return fallback;
  }
  return message;
}

export const STATUS_LABELS = {
  active: 'Active',
  scheduled: 'Scheduled',
  paused: 'Paused',
  expired: 'Expired',
  exhausted: 'Exhausted',
};

export const STATUS_COLORS = {
  active: { bg: '#dcfce7', color: '#16a34a' },
  scheduled: { bg: '#dbeafe', color: '#2563eb' },
  paused: { bg: '#fef3c7', color: '#d97706' },
  expired: { bg: '#eef1f6', color: '#6b7688' },
  exhausted: { bg: '#fee2e2', color: '#dc2626' },
};

export function discountText(coupon) {
  if (coupon.discountType === 'percentage') {
    return `${coupon.discountValue}% off${coupon.maxDiscount ? ` (cap ₹${coupon.maxDiscount})` : ''}`;
  }
  return `₹${coupon.discountValue} off`;
}

export function appliesLabel(value) {
  return { processing_fee: 'Processing fee', emi: 'EMI', foreclosure: 'Foreclosure' }[value] || value;
}

export function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function restrictionsSummary(coupon) {
  const parts = [];
  if (coupon.productIds && coupon.productIds.length) parts.push(`${coupon.productIds.length} product(s)`);
  if (coupon.channel) parts.push(coupon.channel);
  if (coupon.userId) parts.push(`user ${coupon.userId}`);
  return parts;
}

export function toCsv(coupons) {
  const headers = [
    'Code', 'Description', 'DiscountType', 'DiscountValue', 'MaxDiscount', 'AppliesTo',
    'MinLoanAmount', 'ValidFrom', 'ValidUntil', 'MaxTotalUses', 'CurrentUses', 'MaxPerUser',
    'ProductIds', 'Channel', 'Audience', 'UserId', 'IsActive', 'Status',
  ];
  const rows = coupons.map((c) => [
    c.code,
    c.description || '',
    c.discountType,
    c.discountValue,
    c.maxDiscount ?? '',
    c.appliesTo ?? '',
    c.minLoanAmount ?? 0,
    c.validFrom ?? '',
    c.validUntil ?? '',
    c.maxTotalUses ?? '',
    c.currentUses ?? 0,
    c.maxPerUser ?? 1,
    (c.productIds || []).join('|'),
    c.channel || '',
    c.userId ? 'user' : 'public',
    c.userId || '',
    c.isActive ?? '',
    c.status ?? '',
  ]);
  return [headers, ...rows]
    .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
    .join('\n');
}

export function downloadCsv(csv, filename = 'coupons.csv') {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
